import React, { memo } from 'react'
import { HighlightedText } from './HighlightedText'
import { PathItem } from './PathItem'
import { Link } from '@tanstack/react-router'
import { useGetFolderTree } from '@/shared/hooks/search/useGetFolderTree'
import { Skeleton } from '@workspace/ui/components/Skeleton'
import { useInView } from 'react-intersection-observer'
interface FolderItemProps {
    title: string | null
    path?: string | null
    projectId?: string | null
    highlights?: {
        title?: string[]
    }
    onClick?: () => void
    id: string
}

export const FolderItem = memo(({ title, path, highlights, onClick, id, projectId }: FolderItemProps) => {
    const { ref, inView } = useInView({
        threshold: 0,
        triggerOnce: true,
    })
    const { data, isLoading } = useGetFolderTree(id, inView)

    const lastParentFolder = !!data?.parentFolders?.length && {
        name: data?.parentFolders?.at(-1)?.name || '',
        level: data?.parentFolders?.length || 0,
    }

    return (
        <Link
            ref={ref}
            to="/projects/$id/project-profile"
            params={{
                id: projectId || '',
            }}
            search={{
                searchId: id,
                type: 'FOLDER',
            }}
            className="group flex items-start gap-3 py-3 hover:bg-neutral-50 dark:hover:bg-neutral-800 rounded-md px-2"
            onClick={onClick}
        >
            <img src="/svg/folder.svg" alt="Project" className="size-5" />

            <div className="flex flex-col min-w-0 flex-1 gap-2">
                <HighlightedText
                    text={title}
                    highlight={highlights?.title}
                    className="text-sm font-medium text-foreground truncate leading-5 group-hover:underline"
                />

                {isLoading ? (
                    <Skeleton className="h-4 w-48" />
                ) : (
                    <PathItem
                        parentFolder={
                            lastParentFolder
                                ? { name: lastParentFolder?.name || '', level: lastParentFolder?.level || 0 }
                                : undefined
                        }
                        projectName={data?.project?.name || ''}
                    />
                )}
            </div>
        </Link>
    )
})

FolderItem.displayName = 'FolderItem'
